import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { useValidationGroup } from "@/hooks/useValidationGroup";
import {
  DonationFormStateValues,
  getCurrentStateValues,
  useDonationFormStore,
} from "@/stores";
import type { FieldProps } from "./FieldProps";
import { ServerErrors } from "@/types/ServerError";
import { useErrorProcessing } from "@/hooks/useErrorProcessing";
import { getValueByPath } from "@/utils/getValueByPath";

const checkIfChangeByAutocomplete = (
  e: React.ChangeEvent<
    HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
  >,
) => {
  return document.activeElement !== e.target;
};

export const Field = <
  T extends Record<string, unknown> = DonationFormStateValues,
  V = string,
>({
  render,
  name,
  validate,
  dependencies,
  serverErrorPaths,
}: FieldProps<T, V>) => {
  const inputRef = useRef(null);
  const { isValidated, registerValidation, groupName, error, setError } =
    useValidationGroup(name);

  const { registerProcessor, unregisterProcessor } = useErrorProcessing();

  useEffect(() => {
    if (!Array.isArray(serverErrorPaths) || serverErrorPaths.length === 0) {
      return;
    }

    const errorProcessor = {
      name: groupName ? `${groupName}.${name}` : name,
      test: (serverErrors: ServerErrors) => {
        return serverErrorPaths.some(
          (path) =>
            Array.isArray(serverErrors[path]) && serverErrors[path].length > 0,
        );
      },
      execute: (serverErrors: ServerErrors) => {
        const errorMessages = serverErrorPaths
          .map((path) => serverErrors[path])
          .flat();
        setError(errorMessages[0]);
      },
    };

    registerProcessor(errorProcessor);

    return () => {
      unregisterProcessor(errorProcessor);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, groupName]);

  const value = useDonationFormStore((state) => {
    const path = groupName ? `${groupName}.${name}` : name;
    return getValueByPath(state, path) as V;
  });

  const [isTouched, setIsTouched] = useState(() => {
    const hasValue = value !== undefined && value !== null && value !== "";
    return hasValue;
  });

  const [isValid, setIsValid] = useState<boolean | null>(null);
  const setFieldValue = useDonationFormStore((state) => state.setFieldValue);

  const dependencyStringValues = useDonationFormStore((state) => {
    const path = groupName ? `${groupName}.${name}` : name;
    const currentValue = getValueByPath(state, path) as V;

    if (Array.isArray(dependencies) && dependencies.length > 0) {
      return JSON.stringify(
        dependencies
          .map((dep) => {
            const depPath = groupName ? `${groupName}.${dep}` : dep;
            return getValueByPath(state, depPath);
          })
          .concat([currentValue]),
      );
    }
    return JSON.stringify([currentValue]);
  });

  const validateField = useCallback(
    (value: V | undefined) => {
      if (!validate) {
        setError(null);
        setIsValid(true);
        return;
      }

      const currentState = getCurrentStateValues();
      const groupState = (groupName
        ? currentState[groupName]
        : currentState) as unknown as T;

      if (!groupState) {
        throw new Error(`Group state for ${groupName} is undefined`);
      }

      const schema = validate(groupState, currentState);

      if (schema) {
        const result = schema.safeParse(value);
        if (!result.success) {
          const error = result.error.errors[0].message;
          setError(error);
          setIsValid(false);
          return { isValid: false };
        }
      }

      setError(null);
      setIsValid(true);

      return { isValid: true };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [groupName, setError],
  );

  useEffect(() => {
    const unRegisterValidation = registerValidation(name, {
      validate: (currentState) => {
        if (!validate) {
          setError(null);
          setIsValid(true);
          return { isValid: true };
        }

        const currentStateWithIndex = currentState as Record<string, unknown>;
        const groupState = (groupName
          ? currentStateWithIndex[groupName]
          : currentStateWithIndex) as unknown as T;

        if (!groupState) {
          throw new Error(`Group state for ${groupName} is undefined`);
        }

        const value = getValueByPath(groupState, name as string) as V;

        const schema = validate(groupState, currentState);
        if (schema) {
          const result = schema.safeParse(value);
          if (!result.success) {
            const error = result.error.errors[0].message;
            setError(error);
            setIsValid(false);
            return { isValid: false, error };
          }
        }
        setError(null);
        setIsValid(true);
        return { isValid: true };
      },
      ref: inputRef,
    });

    return () => {
      unRegisterValidation();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, groupName]);

  useEffect(() => {
    validateField(value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dependencyStringValues]);

  const setChange = useMemo(() => {
    return (newValue: V) => {
      const path = groupName ? `${groupName}.${name}` : name;
      setFieldValue(path, newValue);
    };
  }, [setFieldValue, groupName, name]);

  const handleBlur = useCallback(() => {
    setIsTouched(true);
  }, []);

  const handleChange = useCallback(
    (
      newValue: V,
      event?: React.ChangeEvent<
        HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
      >,
    ) => {
      const isChangedByAutocomplete = event
        ? checkIfChangeByAutocomplete(event)
        : false;

      if (isChangedByAutocomplete) {
        setIsTouched(true);
      }
      setChange(newValue);
    },
    [setChange],
  );

  const errorMessage = isValidated ? error : null;
  const hasValue = value !== undefined && value !== null && value !== "";

  return render({
    value: (value ?? "") as V,
    handleChange,
    handleBlur,
    isTouched,
    ref: inputRef,
    errorMessage,
    isValid: (isValidated || isTouched) && isValid && hasValue,
  });
};
